import styles from 'css/src/components/common/video-preview/VideoWithScrubber.module.scss';

import React from 'react';

import type { VideoRender } from 'js/src/client';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import { nextPlaybackStateOnSeek } from 'js/src/components/common/video-preview/playbackStateOnSeek';
import SlideMarkerScrubber from 'js/src/components/common/video-preview/SlideMarkerScrubber';
import { PLAYBACK_STATE } from 'js/src/components/common/video-preview/VideoPreview';
import type { NumOpenCommentsPerScene } from 'js/src/data/comments';
import type { DeepReadonly } from 'js/src/libs/consts';
import Timer from 'js/src/libs/timer';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import {
    sceneGroupDurationsWithoutTransitions,
    sceneGroupStartTimes,
    slideEndTimes,
    slideIdsByStartTime,
    slideStartTimes,
} from 'js/src/luminary';
import { useLuminarySelector } from 'js/src/luminary/luminary-hooks';

const getSeekTimeOffset = (
    new_time: number,
    slideStartTimes: number[],
    sceneDurations: number[],
) => {
    let segmentIdx = 0;
    let seekTime = 0;

    slideStartTimes.find((startTime, idx) => {
        const endTime = startTime + sceneDurations[idx];
        if (new_time >= startTime && new_time < endTime) {
            segmentIdx = idx;
            seekTime = new_time - slideStartTimes[idx];
            return true;
        }
        return false;
    });
    return [segmentIdx, seekTime];
};

type VideoWithScrubberOwnProps = {
    autoplay?: boolean;
    sortedVideoSourceList: readonly DeepReadonly<VideoRender>[];
    onSeek?: (newTime: number) => void;
    numOpenCommentsPerScene?: NumOpenCommentsPerScene;
    commentMarkerCallback?: (time: number, forceCommentingMode: boolean) => void;
    playbackState: PLAYBACK_STATE;
    setPlaybackState: (newPlaybackState: PLAYBACK_STATE, callback?: () => void) => void;
    muted: boolean;
    onCurrentTimeUpdate?: (currentTime: number) => void;
    scrubberTooltipTitle?: React.ReactNode;
};

export type VideoWithScrubberHandle = {
    play: (opts?: { firstPlay: boolean }) => Promise<void>;
    pause: () => Promise<void>;
    stop: () => Promise<void>;
    getCurrentTime: () => number;
    getDuration: () => number;
    seek: (time: number) => void;
};

const VideoWithScrubber = React.memo(function VideoWithScrubber({
    autoplay,
    sortedVideoSourceList,
    onSeek,
    numOpenCommentsPerScene,
    commentMarkerCallback,
    playbackState,
    setPlaybackState,
    muted,
    onCurrentTimeUpdate,
    scrubberTooltipTitle,
    ref,
}: VideoWithScrubberOwnProps & { ref?: React.Ref<VideoWithScrubberHandle> }) {
    const {
        duration,
        aspectRatio,
        slideStartTimes: slideStartTimesProp,
        sceneGroupDurationsWithoutTransitions: sceneGroupDurationsWithoutTransitionsProp,
        sceneGroupStartTimes: sceneGroupStartTimesProp,
        slideEndTimes: slideEndTimesProp,
        slideIdsByStartTime: slideIdsByStartTimeProp,
    } = useLuminarySelector(
        (luminary) => ({
            duration: luminary.duration,
            aspectRatio: luminary.aspectRatio,
            slideStartTimes: slideStartTimes(luminary),
            sceneGroupDurationsWithoutTransitions: sceneGroupDurationsWithoutTransitions(luminary),
            sceneGroupStartTimes: sceneGroupStartTimes(luminary),
            slideEndTimes: slideEndTimes(luminary),
            slideIdsByStartTime: slideIdsByStartTime(luminary),
        }),
        [],
    );

    const [currentTime, setCurrentTime] = React.useState(0);
    const [videoState, setVideoState] = React.useState({
        segmentIdx: 0,
        url: sortedVideoSourceList[0]?.https_url,
    });

    const progressBarRef = React.useRef<HTMLSpanElement>(null);
    const publishedVideoRef = React.useRef<HTMLVideoElement>(null);
    const clockTimeoutRef = React.useRef<Timer | null>(null);

    const currentSegmentIdxRef = React.useRef(videoState.segmentIdx);
    const playbackStateRef = React.useRef(playbackState);
    const setTimerRef = React.useRef<() => void>(() => {});

    const playVideoSegmentRef = React.useRef<() => void>(() => {});
    const shouldContinueTimerRef = React.useRef(true);

    React.useEffect(() => {
        currentSegmentIdxRef.current = videoState.segmentIdx;
    }, [videoState.segmentIdx]);

    React.useEffect(() => {
        playbackStateRef.current = playbackState;
    }, [playbackState]);

    const currentUrl = sortedVideoSourceList[videoState.segmentIdx]?.https_url ?? '';
    React.useEffect(() => {
        setVideoState((prev) => ({
            ...prev,
            url: currentUrl,
        }));
    }, [currentUrl]);

    const updateCurrentTime = React.useCallback(
        (time: number) => {
            setCurrentTime(time);
            if (typeof onCurrentTimeUpdate === 'function') {
                onCurrentTimeUpdate(time);
            }
        },
        [onCurrentTimeUpdate],
    );

    const setTimer = React.useCallback(() => {
        const UPDATE_INTERVAL = 100;
        clockTimeoutRef.current = new Timer(() => {
            if (!publishedVideoRef.current) throw new Error('Video element not found');
            let time = publishedVideoRef.current.currentTime * 1000;
            if (currentSegmentIdxRef.current > 0) {
                const sceneGroupStartTime = sceneGroupStartTimesProp[currentSegmentIdxRef.current];
                time += sceneGroupStartTime;
            }
            updateCurrentTime(time);
            if (
                shouldContinueTimerRef.current &&
                playbackStateRef.current === PLAYBACK_STATE.PLAYING
            ) {
                setTimerRef.current();
            }
        }, UPDATE_INTERVAL);
    }, [sceneGroupStartTimesProp, updateCurrentTime]);

    setTimerRef.current = setTimer;

    const playClock = React.useCallback(() => {
        if (clockTimeoutRef.current) {
            clockTimeoutRef.current.resume();
        } else {
            setTimer();
        }
    }, [setTimer]);

    const stopClock = React.useCallback(() => {
        if (clockTimeoutRef.current !== null) {
            clockTimeoutRef.current.cancel();
            clockTimeoutRef.current = null;
        }
        updateCurrentTime(0);
    }, [updateCurrentTime]);

    const pauseClock = React.useCallback(() => {
        if (clockTimeoutRef.current !== null) {
            clockTimeoutRef.current.pause();
        }
    }, []);

    const playVideo = React.useCallback(() => {
        if (publishedVideoRef.current) {
            publishedVideoRef.current.play();
        }
    }, []);

    const pauseVideo = React.useCallback(() => {
        if (publishedVideoRef.current) {
            publishedVideoRef.current.pause();
        }
    }, []);

    const instantSeekProgressBar = React.useCallback(
        (newTime: number) => {
            if (progressBarRef.current) {
                progressBarRef.current.style.transitionDuration = '0.1s';
            }
            updateCurrentTime(newTime);
            const nextState = nextPlaybackStateOnSeek(playbackStateRef.current, newTime, duration);
            if (nextState !== playbackStateRef.current) {
                setPlaybackState(nextState);
            }
            if (playbackStateRef.current === PLAYBACK_STATE.PLAYING) {
                playClock();
            }
        },
        [updateCurrentTime, playClock, duration, setPlaybackState],
    );

    const handleSeekForStoryVideos = React.useCallback(
        (newTime: number) => {
            const [segmentIdx, seekTime] = getSeekTimeOffset(
                newTime,
                sceneGroupStartTimesProp,
                sceneGroupDurationsWithoutTransitionsProp,
            );
            setVideoState({
                segmentIdx,
                url: sortedVideoSourceList[segmentIdx]?.https_url ?? '',
            });
            setTimeout(() => {
                if (!publishedVideoRef.current) throw new Error('Video element not found');
                publishedVideoRef.current.currentTime = seekTime / 1000;
                playbackStateRef.current === PLAYBACK_STATE.PLAYING
                    ? publishedVideoRef.current.play()
                    : publishedVideoRef.current.pause();
            }, 0);
        },
        [
            sceneGroupStartTimesProp,
            sceneGroupDurationsWithoutTransitionsProp,
            sortedVideoSourceList,
        ],
    );

    const seek = React.useCallback(
        (newTime: number) => {
            instantSeekProgressBar(newTime);
            typeof onSeek === 'function' && onSeek(newTime);

            if (sortedVideoSourceList.length > 1) {
                handleSeekForStoryVideos(newTime);
            } else {
                if (!publishedVideoRef.current) throw new Error('Video element not found');
                publishedVideoRef.current.currentTime = newTime / 1000;
            }
        },
        [instantSeekProgressBar, onSeek, sortedVideoSourceList.length, handleSeekForStoryVideos],
    );

    const play = React.useCallback(
        (opts: { firstPlay: boolean } = { firstPlay: false }) => {
            return new Promise<void>((resolve) => {
                const shouldResetToStart =
                    opts.firstPlay || playbackStateRef.current === PLAYBACK_STATE.STOPPED;

                if (shouldResetToStart) {
                    setVideoState({
                        segmentIdx: 0,
                        url: sortedVideoSourceList[0]?.https_url ?? '',
                    });
                    setTimeout(() => {
                        setPlaybackState(PLAYBACK_STATE.PLAYING, () => {
                            playClock();
                            playVideo();
                            resolve();
                        });
                    }, 0);
                } else {
                    setPlaybackState(PLAYBACK_STATE.PLAYING, () => {
                        playClock();
                        playVideo();
                        resolve();
                    });
                }
            });
        },
        [sortedVideoSourceList, setPlaybackState, playClock, playVideo],
    );

    const pause = React.useCallback(() => {
        return new Promise<void>((resolve) => {
            setPlaybackState(PLAYBACK_STATE.PAUSED, () => {
                pauseClock();
                pauseVideo();
                resolve();
            });
        });
    }, [setPlaybackState, pauseClock, pauseVideo]);

    const stop = React.useCallback(() => {
        return new Promise<void>((resolve) => {
            updateCurrentTime(0);
            setPlaybackState(PLAYBACK_STATE.STOPPED, () => {
                stopClock();
                pauseVideo();
                resolve();
            });
        });
    }, [updateCurrentTime, setPlaybackState, stopClock, pauseVideo]);

    const playVideoSegment = React.useCallback(() => {
        const nextSegmentIdx = currentSegmentIdxRef.current + 1;
        if (nextSegmentIdx >= sortedVideoSourceList.length) {
            setPlaybackState(PLAYBACK_STATE.STOPPED, () => {
                stopClock();
            });
        } else {
            setVideoState({
                segmentIdx: nextSegmentIdx,
                url: sortedVideoSourceList[nextSegmentIdx]?.https_url ?? '',
            });
            setTimeout(() => {
                setPlaybackState(PLAYBACK_STATE.PLAYING, () => {
                    if (!publishedVideoRef.current) throw new Error('Video element not found');
                    publishedVideoRef.current.play();
                });
            }, 0);
        }
    }, [sortedVideoSourceList, setPlaybackState, stopClock]);

    playVideoSegmentRef.current = playVideoSegment;

    const togglePlay = React.useCallback(() => {
        if (playbackStateRef.current !== PLAYBACK_STATE.PLAYING) {
            play();
        } else {
            pause();
        }
    }, [play, pause]);

    React.useImperativeHandle(
        ref,
        () => ({
            play,
            pause,
            stop,
            getCurrentTime: () => currentTime,
            getDuration: () => duration,
            seek,
        }),
        [play, pause, stop, currentTime, duration, seek],
    );

    const stablePlayVideoSegment = React.useCallback(() => {
        playVideoSegmentRef.current();
    }, []);

    React.useEffect(() => {
        const videoElement = publishedVideoRef.current;
        if (videoElement) {
            videoElement.addEventListener('ended', stablePlayVideoSegment);
            return () => {
                videoElement.removeEventListener('ended', stablePlayVideoSegment);
            };
        }
    }, [stablePlayVideoSegment, videoState.url]);

    React.useEffect(() => {
        return () => {
            shouldContinueTimerRef.current = false;
            if (clockTimeoutRef.current !== null) {
                clockTimeoutRef.current.cancel();
                clockTimeoutRef.current = null;
            }
        };
    }, []);

    const getProgressBarPercentage = (totalRuntime: number) => {
        return (currentTime * 100.0) / totalRuntime;
    };

    const progressScrubber = (
        <div className={`d-flex align-items-center ${styles['slide-meta']}`}>
            <div className="progress slide-meta-overlay">
                <span
                    ref={progressBarRef}
                    className="progress-bar progress-bar-grey progress-bar-second-transitions w-100"
                    style={{
                        transform: `scaleX(${getProgressBarPercentage(duration) / 100})`,
                    }}
                />
            </div>
            <SlideMarkerScrubber
                slideStartTimes={slideStartTimesProp}
                slideEndTimes={slideEndTimesProp}
                slideIdsByStartTime={slideIdsByStartTimeProp}
                numOpenCommentsPerScene={numOpenCommentsPerScene}
                duration={duration}
                seek={seek}
                commentMarkerCallback={commentMarkerCallback}
            />
            <div className={`position-absolute start-0 ms-l5-8 pe-none ${styles['blur-text-bkg']}`}>
                {getTimeStringFromSeconds(currentTime / 1000)}
            </div>
            <div className={`position-absolute end-0 me-l5-8 pe-none ${styles['blur-text-bkg']}`}>
                {getTimeStringFromSeconds(duration / 1000)}
            </div>
        </div>
    );

    return (
        <div className="col-md-12 video-with-scrubber">
            <div className="slide-corner-radius slide-shadow">
                <div className="video-preview position-relative mx-l5-auto my-l5-0 w-100">
                    <div
                        onClick={togglePlay}
                        className={`luminary-canvas top-corner-radius ${aspectRatio}`}
                    >
                        <video
                            className="w-100 luminary-preview"
                            key={videoState.url}
                            ref={publishedVideoRef}
                            muted={muted}
                            autoPlay={autoplay}
                            poster={
                                videoState.segmentIdx === 0
                                    ? (sortedVideoSourceList[0]?.placeholder_image_url ?? '')
                                    : undefined
                            }
                            playsInline
                            disablePictureInPicture
                            disableRemotePlayback
                        >
                            <source src={videoState.url} />
                        </video>
                    </div>
                </div>

                {scrubberTooltipTitle ? (
                    <Tooltip title={scrubberTooltipTitle}>{progressScrubber}</Tooltip>
                ) : (
                    progressScrubber
                )}
            </div>
        </div>
    );
});

export default VideoWithScrubber;
